// client/src/pages/Admin/ExamsList/index.jsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import customFetch from '../../api/customFetch';

export default function AdminExamsList() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filter, setFilter] = useState('all'); // all, active, inactive
  const nav = useNavigate();

  useEffect(() => {
    loadExams();
  }, []);

  const loadExams = async () => {
    try {
      setLoading(true);
      const { data } = await customFetch.get('/exams/');
      
      if (data.success) {
        setExams(data.exams || []);
      } else {
        setError('שגיאה בטעינת המבחנים');
      }
    } catch (error) {
      console.error('Error loading exams:', error);
      if (error.response?.status === 401) {
        nav('/admin/login');
      } else {
        setError('שגיאה בטעינת רשימת המבחנים');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleExamStatus = async (slug, currentStatus) => {
    try {
      await customFetch.patch(`/exams/${slug}/status`, {
        isActive: !currentStatus
      });
      
      // עדכון הרשימה
      setExams(exams.map(exam => 
        exam.slug === slug 
          ? { ...exam, isActive: !currentStatus }
          : exam
      ));
    } catch (error) {
      console.error('Error toggling exam status:', error);
      setError('שגיאה בשינוי סטטוס המבחן');
    }
  };

  const duplicateExam = async (exam) => {
    try {
      const duplicatedExam = {
        ...exam,
        slug: `${exam.slug}-copy-${Date.now()}`,
        title: `${exam.title} - עותק`,
        isActive: false
      };
      
      // הסרת שדות שלא צריכים להעתק
      delete duplicatedExam._id;
      delete duplicatedExam.createdAt;
      delete duplicatedExam.updatedAt;
      delete duplicatedExam.createdBy;
      delete duplicatedExam.stats;

      const { data } = await customFetch.post('/exams/', duplicatedExam);
      
      if (data.success) {
        nav(`/admin/exams/${data.exam.slug}`);
      }
    } catch (error) {
      console.error('Error duplicating exam:', error);
      setError('שגיאה בשכפול המבחן');
    }
  };

  // סינון ומיון
  const filteredAndSortedExams = exams
    .filter(exam => {
      if (filter === 'active') return exam.isActive;
      if (filter === 'inactive') return !exam.isActive;
      return true;
    })
    .sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      // טיפול מיוחד בתאריכים
      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const getStatusBadge = (exam) => {
    if (!exam.isActive) {
      return { text: 'לא פעיל', color: '#e74c3c' };
    }
    return { text: 'פעיל', color: '#27ae60' };
  };

  if (loading) {
    return (
      <div className="page" dir="rtl">
        <h2>ניהול מבחנים</h2>
        <div>טוען...</div>
      </div>
    );
  }

  return (
    <div className="page" dir="rtl">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{color: 'white'}}>ניהול מבחנים ({filteredAndSortedExams.length})</h2>
        <div>
          <button 
            className="btn"
            onClick={() => nav('/admin/exams/new')}
            style={{ marginLeft: '10px' }}
          >
            + מבחן חדש
          </button>
          <button 
            className="btn"
            onClick={() => nav('/admin/courses')}
            style={{ marginLeft: '10px' }}
          >
            ניהול לומדות
          </button>
          <button 
            className="btn"
            onClick={() => nav('/admin/users')}
          >
            ניהול משתמשים
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#ffebe9',
          color: '#86181d',
          padding: '10px 12px',
          borderRadius: 12,
          marginBottom: 12,
          border: '1px solid #ffd1d1'
        }}>
          {error}
          <button 
            onClick={() => setError(null)}
            style={{ float: 'left', background: 'none', border: 'none', color: '#86181d' }}
          >
            ✕
          </button>
        </div>
      )}

      {/* פילטרים ומיון */}
      <div className="card" style={{ padding: '20px', marginBottom: '20px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          alignItems: 'end'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              סינון
            </label>
            <select 
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="all">כל המבחנים</option>
              <option value="active">פעילים</option>
              <option value="inactive">לא פעילים</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              מיון לפי
            </label>
            <select 
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="createdAt">תאריך יצירה</option>
              <option value="title">שם המבחן</option>
              <option value="passScore">ציון עובר</option>
              <option value="stats.totalAttempts">מספר ניסיונות</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              סדר
            </label>
            <select 
              value={sortOrder}
              onChange={e => setSortOrder(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="desc">יורד</option>
              <option value="asc">עולה</option>
            </select>
          </div>

          <div>
            <button
              className="btn"
              onClick={loadExams}
              // style={{ background: 'var(--text)', color: 'var(--bg)' }}
            >
              רענן רשימה
            </button>
          </div>
        </div>
      </div>

      {/* רשימת המבחנים */}
      {filteredAndSortedExams.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <h3>אין מבחנים</h3>
          <p style={{ color: 'var(--text)' }}>
            {filter === 'all' ? 'לא נוצרו מבחנים עדיין' : 'אין מבחנים המתאימים לסינון הנוכחי'}
          </p>
          {filter === 'all' && (
            <button 
              className="btn"
              onClick={() => nav('/admin/exams/new')}
              style={{ marginTop: '20px' }}
            >
              צור מבחן ראשון
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {filteredAndSortedExams.map(exam => {
            const status = getStatusBadge(exam);
            
            return (
              <div 
                key={exam._id}
                className="card"
                style={{ 
                  padding: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: `2px solid ${status.color}`
                }}
                onClick={() => nav(`/admin/exams/${exam.slug}`)}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0px)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: '20px',
                  alignItems: 'start'
                }}>
                  {/* פרטי המבחן */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <h3 style={{ margin: 0, color: '#ffffff' }}>
                        {exam.title}
                      </h3>
                      <span style={{
                        background: status.color,
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: '500'
                      }}>
                        {status.text}
                      </span>
                    </div>

                    <div style={{ fontSize: '0.9rem', color: 'var(--text)', marginBottom: '8px' }}>
                      <strong>מזהה:</strong> {exam.slug}
                    </div>

                    {exam.description && (
                      <p style={{ color: 'var(--text)', fontSize: '0.9rem', margin: '8px 0' }}>
                        {exam.description}
                      </p>
                    )}

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '12px',
                      marginTop: '12px'
                    }}>
                      <div>
                        <strong>שאלות:</strong> {exam.questions?.length || 0}
                      </div>
                      <div>
                        <strong>ציון עובר:</strong> {exam.passScore}%
                      </div>
                      <div>
                        <strong>זמן:</strong> {exam.timeLimit ? `${exam.timeLimit} דקות` : 'ללא הגבלה'}
                      </div>
                      <div>
                        <strong>ניסיונות:</strong> {exam.stats?.totalAttempts || 0}
                      </div>
                    </div>

                    <div style={{
                      fontSize: '0.8rem',
                      color: 'var(--text)',
                      marginTop: '8px'
                    }}>
                      נוצר: {new Date(exam.createdAt).toLocaleDateString('he-IL')} | 
                      עודכן: {new Date(exam.updatedAt).toLocaleDateString('he-IL')}
                    </div>

                    {/* סקטורים */}
                    {exam.targetSectors && exam.targetSectors.length > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        <strong style={{ fontSize: '0.8rem' }}>סקטורים: </strong>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text)' }}>
                          {exam.targetSectors.join(', ')}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* פעולות */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    minWidth: '120px'
                  }}>
                    <button
                      className="btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        nav(`/admin/exams/${exam.slug}`);
                      }}
                      style={{
                        background: 'var(--accent)',
                        color: '#0b1c2b',
                        fontSize: '0.8rem',
                        padding: '6px 12px'
                      }}
                    >
                      ערוך
                    </button>

                    <button
                      className="btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        nav(`/admin/exams/${exam.slug}/results`);
                      }}
                      style={{
                        background: '#3498db',
                        color: 'white',
                        fontSize: '0.8rem',
                        padding: '6px 12px'
                      }}
                    >
                      תוצאות
                    </button>

                    <button
                      className="btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateExam(exam);
                      }}
                      style={{
                        background: '#9b59b6',
                        color: 'white',
                        fontSize: '0.8rem',
                        padding: '6px 12px'
                      }}
                    >
                      שכפל
                    </button>

                    <button
                      className="btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExamStatus(exam.slug, exam.isActive);
                      }}
                      style={{
                        background: exam.isActive ? '#e74c3c' : '#27ae60',
                        color: 'white',
                        fontSize: '0.8rem',
                        padding: '6px 12px'
                      }}
                    >
                      {exam.isActive ? 'השבת' : 'הפעל'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* סטטיסטיקות כלליות */}
      {exams.length > 0 && (
        <div className="card" style={{ marginTop: '30px', background: '#2c3e50', padding: '20px' }}>
          <h3 style={{ marginTop: 0, color: '#ffffff', textAlign: 'center' }}>סטטיסטיקות כלליות</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3498db' }}>
                {exams.length}
              </div>
              <div style={{ color: '#ffffff', fontSize: '1rem' }}>סה"כ מבחנים</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#27ae60' }}>
                {exams.filter(e => e.isActive).length}
              </div>
              <div style={{ color: '#ffffff', fontSize: '1rem' }}>מבחנים פעילים</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f39c12' }}>
                {exams.reduce((sum, e) => sum + (e.questions?.length || 0), 0)}
              </div>
              <div style={{ color: '#ffffff', fontSize: '1rem' }}>סה"כ שאלות</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#9b59b6' }}>
                {exams.reduce((sum, e) => sum + (e.stats?.totalAttempts || 0), 0)}
              </div>
              <div style={{ color: '#ffffff', fontSize: '1rem' }}>סה"כ ניסיונות</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}